import React, { useCallback, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { TabPaneElement } from "../../../ValidatableFormTabs/ValidatableFormTabs"
import { Formik } from "formik"
import Tab from "react-bootstrap/Tab"
import { AgentType, RoutingAgentValues } from "../../../../models/agent"
import { getDefaultRoutingAgentValues, getValuesFromRoutingAgent } from "../../../../utility/agents/agentValues"
import { useAppDispatch, useAppSelector } from "../../../../store/hooks"
import { AgentFormProps } from "../../AgentForm"
import FormikRoutingAgentFormGeneral from "./RoutingAgentFormGeneral"
import FormikRoutingAgentFormAdvanced from "./RoutingAgentAdvanced"
import { buildCreateRoutingAgentRequest, buildUpdateRoutingAgentRequest } from "../../../../utility/agents/agentRequest"
import { createAgent, getAgentDeclarations, updateAgent } from "../../../../store/agents/thunks"
import { selectCurrentProjectId } from "../../../../store/projects/selectors"
import OnSubmitValidationError from "../../../OnSubmitValidationError/OnSubmitValidationError"
import { selectAgentDeclarations } from "../../../../store/agents/selectors"
import AgentFormLoader from "../../../AgentForm/AgentFormLoader"
import { getValidationSchema } from "../../../../utility/agents/agentValidation"

const RoutingAgentForm: React.FC<AgentFormProps> = props => {
    const { t } = useTranslation()
    const { agent, tabEntries, submitCallback, validateTabs, ownedByThisScenario } = props

    const projectId = useAppSelector(selectCurrentProjectId)
    const declarations = useAppSelector(selectAgentDeclarations)
    const dispatch = useAppDispatch()

    const mainRef = React.useRef<TabPaneElement>(null)
    const advancedRef = React.useRef<TabPaneElement>(null)

    const validateOperatorQueueTabs = useCallback(() => {
        validateTabs && validateTabs([mainRef])
    }, [validateTabs])

    const initialValues = useMemo(
        () =>
            declarations && declarations[AgentType.RoutingAgent]
                ? agent
                    ? getValuesFromRoutingAgent(agent, declarations[AgentType.RoutingAgent])
                    : getDefaultRoutingAgentValues(declarations[AgentType.RoutingAgent])
                : null,
        [agent, declarations]
    )

    const validationSchema = useMemo(
        () => getValidationSchema(AgentType.RoutingAgent, ownedByThisScenario),
        [ownedByThisScenario]
    )

    useEffect(() => {
        if (projectId && !declarations?.[AgentType.RoutingAgent]) {
            dispatch(getAgentDeclarations(AgentType.RoutingAgent))
        }
    }, [projectId, dispatch, declarations])

    const handleSubmit = useCallback(
        (values: RoutingAgentValues) => {
            if (projectId) {
                if (agent) {
                    const request = buildUpdateRoutingAgentRequest(values)
                    dispatch(updateAgent(projectId, agent.Id, request, submitCallback))
                } else {
                    const request = buildCreateRoutingAgentRequest(values)
                    dispatch(createAgent(projectId, values.Id, request, submitCallback))
                }
            }
        },
        [projectId, agent, dispatch, submitCallback]
    )

    if (!projectId) {
        return null
    }

    if (!initialValues) {
        return <AgentFormLoader count={3} />
    }

    return (
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {formikProps => (
                <>
                    <OnSubmitValidationError formikProps={formikProps} onCallback={validateOperatorQueueTabs} />
                    <Tab.Pane eventKey={tabEntries[0].key} ref={mainRef}>
                        <FormikRoutingAgentFormGeneral {...props} {...formikProps} t={t} />
                    </Tab.Pane>
                    <Tab.Pane eventKey={tabEntries[1].key} ref={advancedRef}>
                        <FormikRoutingAgentFormAdvanced {...props} {...formikProps} t={t} />
                    </Tab.Pane>
                </>
            )}
        </Formik>
    )
}

export default RoutingAgentForm
